import type { ServiceStatus, Status } from '@artifax/shared';
import { STATUS_COLOR, STATUS_INK, STATUS_LABEL } from '../constants.js';
import { dayTooltip, formatUptime } from '../format.js';

interface Props {
  name: string;
  desc: string;
  service: ServiceStatus;
}

/** One service row: name + status pill + 90-day bars + uptime caption. */
export function ServiceRow({ name, desc, service }: Props) {
  const dotColor = STATUS_COLOR[service.status];
  const inkColor = STATUS_INK[service.status];

  // Accessible summary of the 90-day bar strip (titles aren't read by SR).
  const counts = service.history.reduce<Record<Status, number>>(
    (acc, d) => {
      acc[d.status] += 1;
      return acc;
    },
    { operational: 0, degraded: 0, partial: 0, major: 0, maintenance: 0 },
  );
  const summary = (Object.keys(counts) as Status[])
    .filter((s) => counts[s] > 0)
    .map((s) => `${counts[s]} ${STATUS_LABEL[s].toLowerCase()}`)
    .join(', ');

  return (
    <div className="svc">
      <div className="svc__top">
        <div style={{ minWidth: 0 }}>
          <h3 className="svc__name">{name}</h3>
          <p className="svc__desc">{desc}</p>
        </div>
        <p className="pill">
          <span className="pill__dot" style={{ background: dotColor }} aria-hidden="true" />
          <span className="pill__label" style={{ color: inkColor }}>
            {STATUS_LABEL[service.status]}
          </span>
        </p>
      </div>
      <div
        className="bars"
        role="img"
        aria-label={`90-day history: ${summary || 'no data yet'}.`}
      >
        {service.history.map((day) => (
          <div
            key={day.date}
            className="bars__day"
            title={dayTooltip(day)}
            aria-hidden="true"
            style={{ background: STATUS_COLOR[day.status] }}
          />
        ))}
      </div>
      <div className="bars__caption">
        <span>90 days ago</span>
        <strong>{formatUptime(service.uptime90)} uptime</strong>
        <span>Today</span>
      </div>
    </div>
  );
}
